/*
 * armada-date-field.js — a segmented date input (AAS-5615).
 *
 * Why not <input type="date">: the native control orders its segments by the
 * BROWSER locale, not the storefront market — a German market in an en-US
 * browser shows mm/dd/yyyy — and there is no attribute to override it. The
 * segment order here comes from the market and is rendered by Liquid, so it is
 * right on the first paint with no JS involved.
 *
 * Why no calendar: a date of birth is a date the visitor remembers, not one
 * they pick. Nobody navigates a month grid back to 1984. Every major public
 * design system asks for it as separate day / month / year fields for that
 * reason — GOV.UK, the NHS service manual, USWDS, CMS — and GOV.UK's own user
 * research found a typed year beat a dropdown: people finished faster and were
 * relieved not to scroll. So this keeps only what the native control does well:
 * one box, segments typed straight into, auto-advance, and a placeholder
 * ("Date of birth") while the box is empty and unfocused.
 *
 * Markup contract (see sections/newsletter-signup-form.liquid):
 *   <armada-date-field data-min="1900-01-01" data-max="YYYY-MM-DD">
 *     <div data-date-box>                        the visual box: segments and separators
 *       <input data-date-part="day|month|year">  ×3, in the market's order
 *     </div>
 *     <input type="hidden" data-date-value>      what the form submits: ISO when the
 *                                                segments form a date, the raw
 *                                                segments otherwise
 *   </armada-date-field>
 *
 * `parse()` is the public contract for the form: '' while every segment is
 * empty, 'YYYY-MM-DD' for a real date inside min..max, null otherwise.
 * `segments` (day, month, year inputs) lets the form flag and focus them.
 */

class ArmadaDateField extends HTMLElement {
  connectedCallback() {
    this.box = this.querySelector('[data-date-box]');
    this.valueInput = this.querySelector('[data-date-value]');
    this.segments = {};
    this.querySelectorAll('[data-date-part]').forEach((el) => {
      this.segments[el.dataset.datePart] = el;
    });
    if (!this.box || !this.segments.day || !this.segments.month || !this.segments.year) {
      return;
    }
    this.ordered = Array.from(this.querySelectorAll('[data-date-part]'));

    this.min = this.fromIso(this.dataset.min) || new Date(1900, 0, 1);
    this.max = this.fromIso(this.dataset.max) || this.today();

    this.onBoxClick = this.onBoxClick.bind(this);
    this.onSegmentInput = this.onSegmentInput.bind(this);
    this.onSegmentKeydown = this.onSegmentKeydown.bind(this);
    this.onSegmentBlur = this.onSegmentBlur.bind(this);
    this.onFormReset = this.onFormReset.bind(this);

    this.box.addEventListener('click', this.onBoxClick);
    this.ordered.forEach((el) => {
      el.addEventListener('input', this.onSegmentInput);
      el.addEventListener('keydown', this.onSegmentKeydown);
      el.addEventListener('blur', this.onSegmentBlur);
    });
    this.closest('form')?.addEventListener('reset', this.onFormReset);

    this.sync();
  }

  disconnectedCallback() {
    this.closest('form')?.removeEventListener('reset', this.onFormReset);
  }

  /* ---------- segments ---------- */

  // A click on the box (its padding, the placeholder, a separator) lands in
  // the first empty segment, or the first one.
  onBoxClick(event) {
    if (event.target.closest('[data-date-part]')) return;
    (this.ordered.find((el) => !el.value) || this.ordered[0]).focus();
  }

  onSegmentInput(event) {
    const el = event.target;
    const width = Number(el.maxLength) || 2;
    el.value = el.value.replace(/\D/g, '').slice(0, width);
    this.sync();
    // Auto-advance once the segment is full, or once a day/month digit can
    // only be a complete value (4-9 for a day, 2-9 for a month).
    const first = Number(el.value[0]);
    const complete =
      el.value.length === width ||
      (el.value.length === 1 && ((el.dataset.datePart === 'day' && first > 3) || (el.dataset.datePart === 'month' && first > 1)));
    if (complete) {
      if (el.value.length === 1) el.value = el.value.padStart(2, '0');
      this.sync();
      this.next(el)?.focus();
    }
  }

  onSegmentKeydown(event) {
    const el = event.target;
    const caretAtStart = el.selectionStart === 0 && el.selectionEnd === 0;
    const caretAtEnd = el.selectionStart === el.value.length && el.selectionEnd === el.value.length;
    if (event.key === 'Backspace' && !el.value) {
      event.preventDefault();
      this.prev(el)?.focus();
    } else if (event.key === 'ArrowLeft' && caretAtStart) {
      event.preventDefault();
      this.prev(el)?.focus();
    } else if (event.key === 'ArrowRight' && caretAtEnd) {
      event.preventDefault();
      this.next(el)?.focus();
    } else if (event.key === '/' || event.key === '.' || event.key === '-' || event.key === ' ') {
      event.preventDefault();
      if (el.value) this.next(el)?.focus();
    }
  }

  // "5" typed and left behind becomes "05" — what the user meant, and what
  // the ISO value needs.
  onSegmentBlur(event) {
    const el = event.target;
    if (el.value.length === 1 && el.dataset.datePart !== 'year') {
      el.value = el.value.padStart(2, '0');
      this.sync();
    }
  }

  prev(el) {
    return this.ordered[this.ordered.indexOf(el) - 1] || null;
  }

  next(el) {
    return this.ordered[this.ordered.indexOf(el) + 1] || null;
  }

  // Mirror the segments into the hidden field and the box's state class.
  sync() {
    const iso = this.parse();
    const typed = this.ordered.some((el) => el.value !== '');
    const raw = typed ? this.ordered.map((el) => el.value).join('/') : '';
    if (this.valueInput) this.valueInput.value = iso || raw;
    this.box.classList.toggle('has-value', typed);
  }

  // '' while every segment is empty, YYYY-MM-DD for a real date in range,
  // null otherwise.
  parse() {
    const day = this.segments.day.value.trim();
    const month = this.segments.month.value.trim();
    const year = this.segments.year.value.trim();
    if (!day && !month && !year) return '';
    if (!/^\d{1,2}$/.test(day) || !/^\d{1,2}$/.test(month) || !/^\d{4}$/.test(year)) return null;

    const y = Number(year);
    const m = Number(month);
    const d = Number(day);
    // Date rolls an impossible day over (31 April → 1 May) instead of
    // rejecting it; comparing back catches that.
    const date = new Date(y, m - 1, d);
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
    if (date < this.min || date > this.max) return null;
    return this.toIso(date);
  }

  onFormReset() {
    // The reset event fires before form.reset() clears the segments; sync on
    // the next task, once they are empty. (Not requestAnimationFrame: it does
    // not run while the tab is in the background.)
    setTimeout(() => this.sync(), 0);
  }

  /* ---------- dates ---------- */

  today() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  toIso(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  fromIso(iso) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
    if (!match) return null;
    const [y, m, d] = match.slice(1).map(Number);
    return new Date(y, m - 1, d);
  }
}

if (!customElements.get('armada-date-field')) {
  customElements.define('armada-date-field', ArmadaDateField);
}
